package app.nestbridge.feature.invitations.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import app.nestbridge.domain.identity.usecase.GetCurrentUserProfileUseCase
import app.nestbridge.domain.identity.usecase.GetCurrentUserUseCase
import app.nestbridge.feature.children.usecase.GetChildUseCase
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.model.InvitationDeliveryMethod
import app.nestbridge.feature.invitations.model.InvitationStatus
import app.nestbridge.feature.careteam.model.TeamRelationshipRole
import app.nestbridge.feature.invitations.usecase.CreateInvitationUseCase
import app.nestbridge.feature.invitations.screen.SendInvitationEvent
import app.nestbridge.feature.invitations.screen.SendInvitationScreen
import app.nestbridge.feature.invitations.screen.SendInvitationState
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock

@CircuitInject(
    screen = SendInvitationScreen::class,
    scope = AppScope::class,
)
@Inject
class SendInvitationPresenter(
    @Assisted private val screen: SendInvitationScreen,
    @Assisted private val navigator: Navigator,
    private val getChildUseCase: GetChildUseCase,
    private val createInvitationUseCase: CreateInvitationUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getCurrentUserProfileUseCase: GetCurrentUserProfileUseCase,
) : Presenter<SendInvitationState> {

    @Composable
    override fun present(): SendInvitationState {
        val scope = rememberCoroutineScope()

        var childDisplayName by remember { mutableStateOf<String?>(null) }
        var selectedMethod by remember { mutableStateOf(InvitationDeliveryMethod.EMAIL) }
        var emailOrPhone by remember { mutableStateOf("") }
        var isSending by remember { mutableStateOf(false) }
        var errorMessage by remember { mutableStateOf<String?>(null) }

        suspend fun loadChildDisplayName() {
            val child = getChildUseCase(screen.childId)
            childDisplayName = child?.effectiveDisplayName
        }

        fun onEvent(event: SendInvitationEvent) {
            when (event) {
                SendInvitationEvent.OnAppear -> {
                    kotlinx.coroutines.MainScope().launchSafely {
                        loadChildDisplayName()
                    }
                }

                SendInvitationEvent.Back -> navigator.pop()
                is SendInvitationEvent.SelectMethod -> selectedMethod = event.method
                is SendInvitationEvent.UpdateEmailOrPhone -> emailOrPhone = event.value
                SendInvitationEvent.SendInvitation -> {
                    if (isSending) return
                    isSending = true
                    errorMessage = null

                    scope.launch {
                        try {
                            val user = getCurrentUserUseCase() ?: throw IllegalStateException("No user")
                            val userProfile = getCurrentUserProfileUseCase()
                            val child = getChildUseCase(screen.childId) ?: throw IllegalStateException("Child not found")

                            val now = Clock.System.now()
                            val invitation = Invitation(
                                id = "",
                                status = InvitationStatus.PENDING,
                                inviterUserProfileId = user.id,
                                inviterDisplayName = userProfile?.displayName ?: "Parent",
                                inviterOrganizationName = null,
                                inviterOrganizationType = null,
                                childId = child.id,
                                childDisplayName = child.effectiveDisplayName,
                                teamId = null,
                                teamType = null,
                                proposedTeamRole = TeamRelationshipRole.ORGANIZER,
                                grantsChildStewardship = false,
                                deliveryMethod = selectedMethod,
                                deliveryDestination = emailOrPhone,
                                createdAt = now,
                                createdBy = user.id,
                                modifiedAt = now,
                                modifiedBy = user.id,
                            )

                            createInvitationUseCase(invitation).getOrThrow()

                            // Navigate back to child detail
                            navigator.pop()
                        } catch (e: Exception) {
                            errorMessage = "Failed to send invitation. Please try again."
                        } finally {
                            isSending = false
                        }
                    }
                }
            }
        }

        return SendInvitationState(
            childDisplayName = childDisplayName,
            selectedMethod = selectedMethod,
            emailOrPhone = emailOrPhone,
            isSending = isSending,
            errorMessage = errorMessage,
            eventSink = ::onEvent,
        )
    }
}
